import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { apiMembership } from "@/lib/api";
import Header from "@/components/setupfinder/Header";
import Footer from "@/components/setupfinder/Footer";
import Landing from "@/components/setupfinder/Landing";
import { LoginPage, SignupPage } from "@/components/setupfinder/AuthPages";
import ForgotPassword from "@/components/setupfinder/ForgotPassword";
import ResetPassword from "@/components/setupfinder/ResetPassword";
import VerifyEmail from "@/components/setupfinder/VerifyEmail";
import AuthCallback from "@/components/setupfinder/AuthCallback";
import Dashboard from "@/components/setupfinder/Dashboard";
import MembershipPage from "@/components/setupfinder/MembershipPage";
import { PaymentSuccess, PaymentCancel } from "@/components/setupfinder/PaymentPages";
import MarketPage from "@/pages/Market";
import ExpertReview from "@/components/setupfinder/ExpertReview";
import BuyCredits from "@/pages/BuyCredits";
import WeeklyUpdates from "@/pages/WeeklyUpdates";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminReviewDetail from "@/pages/admin/AdminReviewDetail";

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="pt-32 text-center text-xs font-mono uppercase tracking-[0.3em] text-neutral-600">
        loading…
      </div>
    );
  }
  if (!user) {
    const after = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?after=${after}`} replace />;
  }
  if (user.email_verified === false) return <Navigate to="/verify-email" replace />;
  return children;
}

function MemberGate({ children }) {
  const [state, setState] = useState({ loading: true, active: false });
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const m = await apiMembership();
        if (alive) setState({ loading: false, active: !!m.active });
      } catch {
        if (alive) setState({ loading: false, active: false });
      }
    })();
    return () => { alive = false; };
  }, []);
  if (state.loading) {
    return (
      <div className="pt-32 text-center text-xs font-mono uppercase tracking-[0.3em] text-neutral-600">
        checking membership…
      </div>
    );
  }
  if (!state.active) return <Navigate to="/membership" replace />;
  return children;
}

function RequireMember({ children }) {
  return (
    <RequireAuth>
      <MemberGate>{children}</MemberGate>
    </RequireAuth>
  );
}

function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="pt-32 text-center text-xs font-mono uppercase tracking-[0.3em] text-neutral-600">
        loading…
      </div>
    );
  }
  // Quietly bounce non-admins to the homepage — no leak of admin existence.
  if (!user || user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function Shell({ children }) {
  return (
    <div className="min-h-screen text-white grid-bg flex flex-col">
      <Header />
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 md:px-6 pb-20">{children}</main>
      <Footer />
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <Shell><AuthCallback /></Shell>;
  }
  return (
    <Shell>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/auth/callback" element={<AuthCallback />} />

        <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
        <Route path="/membership" element={<RequireAuth><MembershipPage /></RequireAuth>} />
        <Route path="/payment/success" element={<RequireAuth><PaymentSuccess /></RequireAuth>} />
        <Route path="/payment/cancel" element={<RequireAuth><PaymentCancel /></RequireAuth>} />

        <Route path="/analyze" element={<Navigate to="/market" replace />} />
        <Route path="/market" element={<RequireMember><MarketPage /></RequireMember>} />
        <Route path="/expert-review" element={<RequireMember><ExpertReview /></RequireMember>} />
        <Route path="/buy-credits" element={<RequireAuth><BuyCredits /></RequireAuth>} />
        <Route path="/weekly-updates" element={<RequireMember><WeeklyUpdates /></RequireMember>} />

        {/* Admin (hidden — entry via © in footer) */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
        <Route path="/admin/reviews/:id" element={<RequireAdmin><AdminReviewDetail /></RequireAdmin>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Shell>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
      <Toaster theme="dark" position="top-right" />
    </div>
  );
}

export default App;
